import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@atlas/auth";

/**
 * Validate password meets requirements:
 * - At least 8 characters
 * - Contains at least one letter
 * - Contains at least one number or symbol
 */
function validatePassword(password: string): { valid: boolean; message: string } {
  if (password.length < 8) {
    return { valid: false, message: "Password must be at least 8 characters" };
  }

  if (!/[a-zA-Z]/.test(password)) {
    return { valid: false, message: "Password must contain at least one letter" };
  }

  if (!/[0-9!@#$%^&*()_+\-=[\]{}|;':",.<>?/\\`~]/.test(password)) {
    return {
      valid: false,
      message: "Password must contain at least one number or symbol",
    };
  }

  return { valid: true, message: "" };
}

/**
 * Reset password page
 * - Email, code, and new password form
 * - confirmResetPassword on submit
 * - Navigate to login on success
 */
export function ResetPasswordPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmResetPassword } = useAuth();

  // Get email from navigation state if available
  const initialEmail = (location.state as { email?: string })?.email ?? "";

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password strength
    const validation = validatePassword(newPassword);
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmResetPassword(email, code, newPassword);
      // Password reset successful - redirect to login
      navigate("/login", {
        state: { message: "Password reset successful! You can now sign in." },
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to reset password";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-semibold text-center mb-1.5 text-text-primary">
          Set new password
        </h1>
        <p className="text-center text-xs text-text-secondary mb-6">
          Enter the verification code from your email and your new password.
        </p>

        {error && (
          <div className="alert-error mb-3">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label
              htmlFor="email"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input-field"
            />
          </div>

          <div className="mb-3">
            <label
              htmlFor="code"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoComplete="one-time-code"
              placeholder="Enter 6-digit code"
              className="input-field tracking-widest"
            />
          </div>

          <div className="mb-3">
            <label
              htmlFor="newPassword"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="input-field"
            />
            <p className="mt-1.5 text-xs text-text-tertiary">
              At least 8 characters with a letter and number/symbol
            </p>
          </div>

          <div className="mb-4">
            <label
              htmlFor="confirmPassword"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="input-field"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary-lg w-full"
          >
            {isSubmitting ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-text-secondary">
          <Link to="/login" className="link">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
